import { gql } from "graphql-request";
import { IMAGE_FRAGMENT } from "./fragments";

/**
 * Fetches all main MICE detail page data including banners, introductions,
 * informational sections, inquiry forms, and related region details.
 */
export const GET_MICE_DATA = gql`
  ${IMAGE_FRAGMENT}
  query MiceDetail(
    $locale: I18NLocaleCode
    $status: PublicationStatus
    $filters: BasePageFiltersInput
  ) {
    basePages(locale: $locale, status: $status, filters: $filters) {
      item_name
      documentId
      datasource {
        banner: hero_banner_datasources {
          information {
            title
            description
            background_image {
              ...ImageUrlProperties
            }
          }
        }
        introduce: introduction_datasource {
          properties {
            title
            introduction
            detail
            link
            link_text
            link_2
            link_text_2
            note
          }
        }
        information: information_datasource {
          properties {
            title
            description
            image {
              ...ImageUrlProperties
            }
          }
        }
        concept_datasources {
          concept_point_label
          concepts_list
        }
        inquiry_form_datasource {
          main_title
          sub_title
          telephone_label
          telephone_number
          form_button_label
          inquiry_link
          note
          button_label_2
          link_2
          description
        }
        mice_detail_datasources {
          mice_detail_list_datasources {
            detail_list
            text {
              heading
              description
              counter_label
              more_text
            }
          }
        }
        search_result_datasources {
          total_meeting_rooms_label
          max_capacity_label
          types_label
          max_capacity_people_label
          search_result_resource {
            header
            download_pdf_label
            website_link_label
            request_rpf_button_label
            selected_regions
          }
          hotel_list_labels {
            has_onsen_label
            activity_label
            training_specialization_label
            available_for_300_guests_label
          }
          table_header {
            room_name_header
            field_1_header
            field_2_header
            field_3_header
            field_4_header
            field_5_header
            field_6_header
            field_7_header
            field_8_header
            field_9_header
            field_10_header
            field_11_header
            field_12_header
            field_13_header
            field_14_header
          }
        }
      }
      concepts {
        item_name
        documentId
        title
        description
        concept_image_desktop {
          ...ImageUrlProperties
        }
        concept_image_mobile {
          ...ImageUrlProperties
        }
      }
      mice_detail {
        documentId
        item_name
        text {
          top_title
          selected_hotel
        }
        lists_and_images {
          region
          details_image {
            ...ImageUrlProperties
          }
        }
      }
      regions {
        documentId
        labels {
          name
          meetings_hotel_list
        }
      }
    }
  }
`;

/**
 * Fetches all hotel data within given regions for the MICE listing.
 * Includes meeting room details and related data sources.
 */
export const GET_HOTEL_LIST = gql`
  query MiceHotelList(
    $regionDocIds: [ID!]
    $locale: I18NLocaleCode
    $status: PublicationStatus
  ) {
    japanAreas(
      locale: $locale
      status: $status
      filters: { documentId: { in: $regionDocIds } }
      pagination: { limit: -1 }
    ) {
      item_name
      documentId
      hotels(pagination: { limit: -1 }) {
        item_name
        documentId
        slug
        properties {
          hotel_name
          mobile
          floor_plan {
            cloudflare_url
            url
            previewUrl
          }
        }
        redirect {
          external_url
        }
        meetings_space {
          number_of_rooms
          max_capacity
          has_onsen
          has_activity
          has_training_and_conference
          can_accommodate_300
          bottom_phone_number
        }
        meeting_rooms(pagination: { limit: -1 }) {
          item_name
          documentId
          informations {
            conference_room
            location_floor
            size
            ceiling_height
            school
            square_shaped
            island
            buffet
            standing_buffet
            u_shape
            others
            dinner
            tatami
            field_14
            theater
          }
        }
        datasources {
          meeting_rooms_datasources {
            documentId
            information {
              meeting_room_list
              id
            }
          }
        }
      }
    }
  }
`;

// Retrieves all text labels and input field labels for MICE inquiry forms.
export const GET_MICE_FORM_DATA = gql`
  query MiceFormData(
    $locale: I18NLocaleCode
    $status: PublicationStatus
    $lightBoxDatasourceDocId: ID!
  ) {
    sharedDatasourcesReusableLightBoxLightBoxDatasource(
      documentId: $lightBoxDatasourceDocId
      status: $status
      locale: $locale
    ) {
      labels {
        planning_your_meeting_of_event_text
        attach_file_label
        free_quotation_please_fill_text
        first_name_label
        last_name_label
        company_name_label
        email_address_label
        phone_number_label
        meeting_event_information_text
        event_meeting_name_label
        nr_attendees_label
        start_date_label
        end_date_label
        guest_rooms_label
        duration_label
        all_day
        type_of_event_label
        please_select_label
        check_applicable_food_text
        breakfast_label
        lunch_label
        dinner_label
        reception_label
        other_label
        please_tell_us_text
        nr_attendees_label
        submit_label
        free_quotation_text
        terms_and_conditions_text
        thank_you_message_text
        select_location_label
      }
    }
  }
`;
